'use client';

import { useState, useEffect } from 'react';
import { ChevronRight, ExternalLink } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { ResponsiveSheet, SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import type { ImNewInfo } from '@/types/serve-finder';
import { Img, useAssetUrl } from '@/lib/asset-url';
import { BaseCard } from './BaseCard';
import { CardCTA } from './CardCTA';

interface ImNewCardProps {
  imNew: ImNewInfo;
}

export function ImNewCard({ imNew }: ImNewCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const resolve = useAssetUrl();
  const slides = imNew.Slider_Slides;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const slide = slides[currentIndex];

  return (
    <>
      <BaseCard
        variant="standard"
        className="bg-card cursor-pointer text-left"
        onClick={() => setIsOpen(true)}
        header={
          <div className="relative aspect-[16/9] w-full overflow-hidden">
            <AnimatePresence mode="popLayout">
              <motion.img
                key={slide.Image_URL}
                src={resolve(slide.Image_URL)}
                alt=""
                className="absolute inset-0 h-full w-full object-cover"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.2, ease: 'easeInOut' }}
              />
            </AnimatePresence>

            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/5 to-transparent" />

            {/* "I'm New" badge */}
            <div className="absolute top-4 left-4 z-10">
              <span
                className="inline-block px-2.5 py-1 text-sm font-extrabold tracking-wide text-white uppercase"
                style={{ backgroundColor: '#6abf4b' }}
              >
                I&apos;m New
              </span>
            </div>

            {/* Slide indicators */}
            <div className="absolute top-4 right-4 z-10 flex gap-1.5">
              {slides.map((_, i) => (
                <div
                  key={i}
                  className={`h-1 rounded-full transition-all duration-500 ${
                    i === currentIndex ? 'w-5 bg-[#6abf4b]' : 'w-1.5 bg-white/30'
                  }`}
                />
              ))}
            </div>
          </div>
        }
        body={
          <div className="relative flex flex-col p-4 pb-3" style={{ minHeight: 120 }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                className="absolute inset-x-4 top-4"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
              >
                <h3 className="text-foreground text-base leading-tight font-extrabold capitalize">
                  {slide.Heading}
                </h3>
                <p className="text-muted-foreground mt-1.5 text-sm leading-relaxed">
                  {slide.Blurb}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>
        }
        footer={<CardCTA label="Learn More" icon="chevron" useDefaultColors />}
      />

      {/* Detail sheet */}
      <ResponsiveSheet
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="max-w-3xl"
        noPanelPadding
        panelClassName="bg-card"
        header={
          <div className="relative aspect-video w-full overflow-hidden">
            <Img src={imNew.Hero_Image_URL} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/20" />

            <div className="absolute inset-x-0 bottom-0 z-10 p-5">
              <div className="mb-1.5 h-1 w-12 bg-[#6abf4b]" />
              <h2 className="text-2xl leading-tight font-light tracking-wide text-white sm:text-3xl">
                I&apos;m new
              </h2>
              <p className="mt-1 text-sm leading-relaxed text-white/70">{imNew.Tagline}</p>
            </div>
          </div>
        }
      >
        <SheetPage name="main">
          <div>
            <div className="space-y-5 p-5">
              <p className="text-muted-foreground text-sm leading-relaxed">{imNew.Description}</p>

              <div className="space-y-2">
                {imNew.Sections.map((section) => (
                  <a
                    key={section.Label}
                    href={section.URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group border-border bg-background flex w-full items-center justify-between gap-3 border p-3 text-left transition-shadow hover:shadow-md"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-20 shrink-0 overflow-hidden">
                        <Img
                          src={section.Image_URL}
                          alt={section.Label}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div>
                        <p className="text-foreground text-sm font-bold">{section.Label}</p>
                        <p className="text-muted-foreground mt-0.5 text-xs">
                          {section.Description}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
                  </a>
                ))}
              </div>
            </div>

            {/* Learn More footer CTA */}
            <a
              href={imNew.Page_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="border-border flex items-center justify-center gap-2 border-t py-4 text-sm font-semibold tracking-wide text-[var(--brand-secondary)] uppercase transition-opacity hover:opacity-80"
            >
              Learn More
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </SheetPage>
      </ResponsiveSheet>
    </>
  );
}
